'use strict';

const _ = require('lodash');
const moment = require('moment');
const envelopeComponent = require('../components/envelope');
const utils = require('../utils');
const consts = require('../consts');

function fixedEnvelopeFromTransaction(transaction) {
  return envelopeComponent.create({
    id: `${_getApproxBudgetDate(transaction)}#${consts.FIXED_ENVELOPE_TYPE}#${transaction.transactionId}`,
    actuals: [transaction],
    originalAmount: (!transaction.billingAmount ? -transaction.incomeAmount : transaction.billingAmount) || 0,
    type: consts.FIXED_ENVELOPE_TYPE,
    details: {
      businessName: transaction.businessName,
      expense: transaction.expense || consts.DEFAULT_FIXED_EXPENSE_CATEGORY,
      sequence: [transaction],
      sequenceId: transaction.transactionId,
    },
    balanceDate: moment(),
  });
}

function createTrackingCategoryEnvelope(name, icon, amount, trackingCategoryId, budgetDate, frequency) {
  if (frequency === consts.TRACKING_FREQUENCY.WEEKLY) {
    return _createWeeklyEnvelopes(name, icon, amount, trackingCategoryId, budgetDate);
  }
  return [_createMonthlyEnvelope(name, icon, amount, trackingCategoryId, budgetDate)];
}

function _createMonthlyEnvelope(name, icon, amount, trackingCategoryId, budgetDate) {
  return envelopeComponent.create({
    id: `${budgetDate}#${consts.TRACKING_CATEGORY_ENVELOPE_TYPE}#${trackingCategoryId}`,
    actuals: [],
    originalAmount: amount,
    type: consts.TRACKING_CATEGORY_ENVELOPE_TYPE,
    details: {
      trackingCategory: {
        _id: trackingCategoryId,
        isEnabled: true,
        name,
        icon,
      },
    },
  }, new Date());
}

function _createWeeklyEnvelopes(name, icon, amount, trackingCategoryId, budgetDate) {
  const dailyAmount = amount / utils.daysInMonths(budgetDate);
  const weeks = utils.getWeeks(budgetDate);
  return _.map(weeks, week => envelopeComponent.create({
    id: `${budgetDate}#${consts.TRACKING_CATEGORY_ENVELOPE_TYPE}#${trackingCategoryId}#${week.index}`,
    actuals: [],
    originalAmount: utils.precision(dailyAmount * week.days),
    type: consts.TRACKING_CATEGORY_ENVELOPE_TYPE,
    details: {
      weeklyIndex: week.index,
      startDate: week.startDate.format('YYYY-MM-DD'),
      endDate: week.endDate.format('YYYY-MM-DD'),
      trackingCategory: {
        _id: trackingCategoryId,
        isEnabled: true,
        name,
        icon,
      },
    },
  }, new Date()));
}

function _getApproxBudgetDate(transaction) {
  return _momentToBudgetDate(moment(transaction.transactionDate));
}

function _momentToBudgetDate(dateMoment) {
  return `${dateMoment.format('YYYY')}-${dateMoment.format('MM')}`;
}

function findEconomyEnvelope(transactionDate, budgetDate, envelopes) {
  const momentTransactionDate = moment(transactionDate);
  return _.find(envelopes, e => (
    e.type === consts.ECONOMY_ENVELOPE_TYPE &&
    moment(e.details.startDate) <= momentTransactionDate &&
    momentTransactionDate < moment(e.details.endDate)
  ));
}

function findRecurringEnvelope(trackingCategory, envelopes) {
  return _.find(envelopes, e => e.type === consts.RECURRING_ENVELOPE_TYPE && e.details.expenseCategory === trackingCategory);
}

function findTrackingCategoryEnvelope(trackingCategoryId, envelopes) {
  return _.find(envelopes, e => e.type === consts.TRACKING_CATEGORY_ENVELOPE_TYPE && e.details.trackingCategory._id === trackingCategoryId);
}

function findTargetTransactionsByExpenseCategory(cashflow, newExpense) {
  const reucrringEnvelope = findRecurringEnvelope(newExpense, cashflow.envelopes);
  if (reucrringEnvelope) {
    return reucrringEnvelope.actuals;
  }
  return cashflow.variableTransactions;
}

function findTargetTransactionsByTrackingCategory(cashflow, trackingCategoryId) {
  const trackingCategoryEnvelope = findTrackingCategoryEnvelope(trackingCategoryId, cashflow.envelopes);
  if (trackingCategoryEnvelope) {
    return trackingCategoryEnvelope.actuals;
  }
  return cashflow.variableTransactions;
}

function findSourceTransactionsByTransactionId(cashflow, transactionId) {
  const matchInVariable = _.find(cashflow.variableTransactions, transaction => transaction.transactionId === transactionId);
  if (matchInVariable) {
    return cashflow.variableTransactions;
  }

  const matchInExcluded = _.find(cashflow.excluded, transaction => transaction.transactionId === transactionId);
  if (matchInExcluded) {
    return cashflow.excluded;
  }

  const { actuals } = _.find(cashflow.envelopes, isTransactionInEnvelope(transactionId));
  return actuals;
}

function isTransactionInEnvelope(transactionId) {
  return envelope => _.find(envelope.actuals, t => t.transactionId === transactionId);
}

function getTransactionsByBusinessName(cashflow, businessName) {
  const matchingVariableTransactions = _.filter(cashflow.variableTransactions, transaction => transaction.businessName === businessName);
  const matchingNonVariableTransactions = _.chain(cashflow.envelopes).flatMap(e => e.actuals).filter(t => t.businessName === businessName).value();
  return [...matchingNonVariableTransactions, ...matchingVariableTransactions];
}

function getTransactionsByExpense(cashflow, expense) {
  return _.chain(cashflow.envelopes)
    .filter(e => e.type === consts.TRACKING_CATEGORY_ENVELOPE_TYPE)
    .flatMap(e => e.actuals)
    .concat(cashflow.variableTransactions)
    .filter(t => t.expense === expense)
    .value();
}

function getTransactionById(cashflow, transactionId) {
  return _.find(cashflow.allTransactions, transaction => transaction.transactionId === transactionId);
}

module.exports = {
  fixedEnvelopeFromTransaction,
  findEconomyEnvelope,
  getTransactionsByBusinessName,
  getTransactionsByExpense,
  findTargetTransactionsByExpenseCategory,
  findTargetTransactionsByTrackingCategory,
  findSourceTransactionsByTransactionId,
  createTrackingCategoryEnvelope,
  getTransactionById,
};

